import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Toast } from "shared-ui/src/components/Toast";
import { useApi } from "@utils/useApi";
import {
  EcommerceShoppingCartClient,
  UpdateCartItemQuantityRequest,
} from "@lib/ShiOneClient";
import { invalidateCart } from "./cartQueryKeys";
import { showProductRemovedToast } from "./cartToasts";

/**
 * Cart-line mutations for checkout and the full cart page. All cart caches are invalidated
 * via `invalidateCart` so the mini-cart and the full cart page stay in sync.
 */
export function useCartLineMutations() {
  const api = useApi(EcommerceShoppingCartClient);
  const queryClient = useQueryClient();

  const invalidate = () => invalidateCart(queryClient);

  const updateQuantity = useMutation<
    void,
    Error,
    { lineNumber: number; quantity: number; syncWarranty?: boolean }
  >({
    mutationFn: ({ lineNumber, quantity, syncWarranty }) => {
      const body = new UpdateCartItemQuantityRequest({ quantity });
      // Call through `api` so the generated client keeps its `this` binding
      // (the methods rely on `this.baseUrl`); a detached reference loses it.
      return syncWarranty
        ? api.updateDeviceQuantity(lineNumber, body)
        : api.updateShoppingCartItemQuantity(lineNumber, body);
    },
    onError: (error) =>
      Toast(`Failed to update quantity. Reason: ${error?.message}`, true, 7000),
    onSettled: invalidate,
  });

  const removeLine = useMutation<
    void,
    Error,
    { lineNumber: number; productName?: string | null }
  >({
    mutationFn: ({ lineNumber }) => api.deleteShoppingCartItem(lineNumber),
    onSuccess: (_data, { productName }) => showProductRemovedToast(productName),
    onError: (error) =>
      Toast(`Failed to remove item. Reason: ${error?.message}`, true, 7000),
    onSettled: invalidate,
  });

  return { updateQuantity, removeLine };
}

export default useCartLineMutations;
